import {
  isFieldsStep,
  isUnrenderableStep,
  rosterKind,
  stepKind,
  type BondStep,
  type ChangeDeclaration,
} from "@/lib/brokerBonds";
import type { BondRenderField } from "@/lib/bondFields";

// BRK-S162 — a REPEAT request, for an insured who already holds a standing
// program.
//
// BRK-S134 established the first half: a granted, account-sourced preapproval
// means this order runs no checks, so the Checks and Indication chips are
// omitted. What it left in place is the part a broker notices — every fields
// step re-rendered with PMS's prefill, so a repeat bid bond asks them to walk
// past their own business details and their own indemnitor roster, one Continue
// at a time, to change nothing.
//
// The scoping document's answer (§ Repeat requests) is to show what Signal
// already holds and ask ONE question: has any of it changed? Only what they say
// changed opens for edit.
//
// ── WHICH STEP IS "BUSINESS" AND WHICH IS "INDEMNITOR" ──────────────────────
//
// Decided STRUCTURALLY, from what the configuration declares, and never from a
// step's key or title. A fields step that declares `roster: "indemnity"` is the
// indemnitor roster — that is a contract property (PMS-S341 / BRK-S152), and it
// is what the whole roster surface already keys on. Every OTHER fields step is
// business information about the insured.
//
// The alternative — matching `key === "indemnitor"` — is exactly the
// name-inference D-10 rule 1 forbids for controls, and for the same reason: the
// configuration is Greg's to rename, and a rule that reads a label would
// silently reclassify a step the day it was retitled. A configuration with no
// roster step therefore has no indemnitor half, and the question asks about the
// business alone rather than offering a choice that addresses nothing.
//
// ── WHAT A DECLARATION IS NOT ───────────────────────────────────────────────
//
// It is not a claim about the ACCOUNT. Saying "nothing changed" hides steps for
// THIS order; it does not assert anything about the insured's record, and it
// writes nothing to it. The values PMS prefilled are the values that file.

// The WIRE shape lives in `brokerBonds.ts` with the rest of the seam
// (`ChangeDeclaration`) and is re-exported here, so a consumer of this module
// imports one thing. What lives HERE is the four answers a broker picks from and
// which configuration steps each covers — product rules, not contract.
export type { ChangeDeclaration } from "@/lib/brokerBonds";

/** The four answers the question offers, as one value the radio group holds. */
export type ChangeChoice = "none" | "business" | "indemnitors" | "both";

/** The choices in the order they are asked, with the words a broker reads. */
export const CHANGE_CHOICES: readonly {
  value: ChangeChoice;
  label: string;
  /** Rendered under the label. Says what picking it DOES, because the
   *  consequence — which steps open — is the thing a broker is choosing. */
  hint: string;
}[] = [
  {
    value: "none",
    label: "No — everything above is still correct",
    hint: "We’ll use what’s on file and take you straight to the bond details.",
  },
  {
    value: "business",
    label: "The business details have changed",
    hint: "You’ll review and update the business information.",
  },
  {
    value: "indemnitors",
    label: "The indemnitor details have changed",
    hint: "You’ll review and update who indemnifies the bond.",
  },
  {
    value: "both",
    label: "Both have changed",
    hint: "You’ll review and update both.",
  },
];

/** The declaration a choice means. */
export function declarationFor(choice: ChangeChoice): ChangeDeclaration {
  return {
    business: choice === "business" || choice === "both",
    indemnitors: choice === "indemnitors" || choice === "both",
  };
}

/** The choice a stored declaration reads as — so a resumed order shows the
 *  answer it was given rather than an empty radio group. */
export function choiceFor(declaration: ChangeDeclaration): ChangeChoice {
  if (declaration.business && declaration.indemnitors) return "both";
  if (declaration.business) return "business";
  if (declaration.indemnitors) return "indemnitors";
  return "none";
}

/** Which half of the question a step belongs to, or `null` for a step the
 *  question does not cover (a data pull, the preapproval, an unsupported
 *  type — none of which is information the broker restates). */
export type RepeatStepClass = "business" | "indemnitors";

export function repeatStepClass(step: BondStep): RepeatStepClass | null {
  if (stepKind(step) !== "fields") return null;
  // A fields step whose roster this build cannot draw (BRK-S297) is in NEITHER
  // half. It used to fall through to "business", so "the business has not
  // changed" hid it — yet an unknown roster is more likely a list of people
  // than business facts, and hiding it skipped a step the server expects. With
  // no class it is never closed and never summarised: it stays on the spine,
  // where the broker meets BRK-S114's explicit unsupported-step card.
  if (isUnrenderableStep(step)) return null;
  return rosterKind(step) === "indemnity" ? "indemnitors" : "business";
}

/** The fields steps a summary covers, in configuration order. */
export function summarisedSteps(steps: readonly BondStep[]): BondStep[] {
  return steps.filter((step) => repeatStepClass(step) !== null);
}

/**
 * Whether a step OPENS for edit under a declaration.
 *
 * `null` — the broker has not answered yet — closes every summarised step, so
 * the spine behind the summary is the shortest it can be and grows as they
 * answer. A step the question does not cover is untouched: a data pull is not
 * information anybody restates, and hiding one would skip work the
 * configuration declared.
 */
export function stepOpensForEdit(
  step: BondStep,
  declaration: ChangeDeclaration | null,
  /** Steps a refused save has OPENED regardless of the answer (BRK-S268). */
  reopened: ReadonlySet<string> = NONE_REOPENED,
): boolean {
  const cls = repeatStepClass(step);
  if (cls === null) return true;
  if (reopened.has(step.key)) return true;
  if (declaration === null) return false;
  return declaration[cls];
}

const NONE_REOPENED: ReadonlySet<string> = new Set();

/**
 * Does the question have an indemnitor half to ask about?
 *
 * False for a configuration that declares no roster step — and then the two
 * choices naming indemnitors are not offered, rather than offered and
 * addressing nothing.
 */
export function hasIndemnitorHalf(steps: readonly BondStep[]): boolean {
  return steps.some((step) => repeatStepClass(step) === "indemnitors");
}

/** The choices to offer for a configuration — the full four, or the two that
 *  mean anything when there is no roster step. */
export function choicesFor(steps: readonly BondStep[]): typeof CHANGE_CHOICES {
  return hasIndemnitorHalf(steps)
    ? CHANGE_CHOICES
    : CHANGE_CHOICES.filter(
        (c) => c.value === "none" || c.value === "business",
      );
}

/**
 * Fields a repeat request may not edit (BRK-S162).
 *
 * The EIN, and only the EIN. PMS makes a set `account.ein` immutable on every
 * write path (PMS-S462 — a 400 `EIN_IMMUTABLE`), so a control for it is an input
 * whose only possible use is a rejected save. Matched on the declared **format**
 * rather than on a field key, for the same reason `repeatStepClass` reads the
 * roster declaration: a configuration is Greg's to rename, and a rule that read
 * a key would stop firing the day the field was re-pathed.
 *
 * This applies ONLY to the repeat spine. On a first request the EIN is being
 * SET, which is the one write PMS still accepts.
 */
export function isImmutableOnRepeat(field: BondRenderField): boolean {
  return field.format === "ein";
}

/**
 * The editable subset of a step's render fields, for a repeat request.
 *
 * REMOVED rather than disabled: a greyed-out box invites a click that does
 * nothing, and the value has a better home — the summary shows it with the
 * reason it cannot change. The stored value still rides out on the step save
 * untouched, which is what keeps that save from looking like a change to PMS.
 */
export function withoutImmutableFields(
  fields: readonly BondRenderField[],
): BondRenderField[] {
  return fields.filter((field) => !isImmutableOnRepeat(field));
}

/** The immutable fields, for the surface that DOES show them. */
export function immutableFields(
  fields: readonly BondRenderField[],
): BondRenderField[] {
  return fields.filter(isImmutableOnRepeat);
}

// ── A REFUSED SAVE OF A STEP THE ANSWER HID (BRK-S268) ──────────────────────
//
// "Nothing changed" does not mean "nothing is owed". The declaration rides on a
// step save of a summarised step (`confirmRepeatDeclaration`), and the server
// judges that save against everything the step would then hold — so a value
// the account never kept (the owner count, until PMS-S529) is refused on a
// step the broker chose to skip. Aaron's local test, 2026-09-28: the Summary
// printed the server's own sentence, contract path and all, and the broker had
// no way forward.
//
// So a refusal that names fields of a summarised step OPENS that step, marks
// the fields by their configured labels and says one plain sentence. The
// answer the broker gave is left as they gave it — they did not say anything
// changed, they were asked for something missing — and it is filed on the save
// of the reopened step, because the refused save filed nothing.

/** The one sentence a reopened step leads with, per half. Code-owned: a
 *  configured step title is Greg's to rename, and "before we continue" is a
 *  product sentence rather than a label. */
export const REOPENED_NOTICE: Record<RepeatStepClass, string> = {
  business: "We need a little more about the business before we continue.",
  indemnitors:
    "We need a little more about the indemnitors before we continue.",
};

/** What a reopened field says under its input: the configured label marks it
 *  and the checklist names it, so the line only says what to do. */
export const REOPENED_BLANK_REASON = "Please fill this in.";
export const REOPENED_CHECK_REASON = "Please check this answer.";

/**
 * The Summary's line for a refusal it could not send anywhere.
 *
 * Code-owned for the same reason as the notice above: the server's own words
 * for a field-level refusal are a sentence ABOUT a contract path ("Not accepted
 * for this bond order: business.numberOfOwners"), and no rewording of it is a
 * sentence a broker can act on.
 */
export const SUMMARY_REFUSAL_UNPLACED =
  "We couldn’t save your answer just now. Please try again, and contact us if it keeps happening.";

/** `personal[0].ssn` and `personal[].ssn` are the same configured field. */
function configuredSpelling(path: string): string {
  return path.replace(/\[\d*\]/g, "[]");
}

/**
 * Whether a refused PATH is one of the step's configured fields — exactly, or
 * as a member of one (an address is one field drawn from four inputs, and the
 * server may name `business.addresses[].city`).
 */
function stepDeclaresPath(step: BondStep, path: string): boolean {
  if (!isFieldsStep(step)) return false;
  const wanted = configuredSpelling(path);
  return step.fields.some((field) => {
    const own = configuredSpelling(field.path);
    return wanted === own || wanted.startsWith(`${own}.`);
  });
}

/**
 * The summarised step a refusal is about — the FIRST, in configuration order,
 * that declares any path it names — or `null` when it names none of them.
 *
 * Read off the CONFIGURATION's paths, never the step's key or title (the same
 * structural rule `repeatStepClass` follows). A refusal naming nothing a
 * summarised step declares stays where it was raised: sending the broker to a
 * step that cannot show them the problem is a second dead end.
 */
export function refusedSummarisedStep(
  steps: readonly BondStep[],
  paths: readonly string[],
): BondStep | null {
  if (paths.length === 0) return null;
  return (
    summarisedSteps(steps).find((step) =>
      paths.some((path) => stepDeclaresPath(step, path)),
    ) ?? null
  );
}
